import { useMemo } from 'react';

import { Dot, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';

import { useFormatTimeIndicator } from '@/hooks/format/useFormatTimeIndicator';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

import Icon from '@/components/base/Icon';
import TooltipCustom from '@/components/composite/Charts/components/TooltipCustom';

// Components
import ChartWrapper from './ChartWrapper';

const CustomShape = ({ cx, cy, payload, dataConfig, isExport, ...restProps }) => {
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';

    if (!dataConfig?.colors?.[theme] || !payload) {
        return null;
    }

    const { color, r, highlight } = payload;

    if (!color || !r) {
        return null;
    }

    const isBestTimeToPost = [
        'best_time_to_post',
        'stories_best_time_to_post',
        'fans_online',
        'posting_habits'
    ].includes(dataConfig.id);

    return (
        <>
            <Dot cy={cy} cx={cx} r={r} fill={dataConfig.colors[theme][color]} {...restProps} />
            {highlight && (
                <foreignObject x={cx - 6} y={cy - 9} width={r * 2} height={r * 2}>
                    <Icon
                        icon={dataConfig?.iconHighestValue}
                        className="text-xs"
                        style={{
                            color:
                                isBestTimeToPost && theme === 'light'
                                    ? 'white'
                                    : darkModeColorsChart.dark[dataConfig.color?.value]
                        }}
                    />
                </foreignObject>
            )}
        </>
    );
};

CustomShape.propTypes = {
    cx: PropTypes.number,
    cy: PropTypes.number,
    payload: PropTypes.object,
    dataConfig: PropTypes.object,
    isExport: PropTypes.bool
};

const HeatMapChartComponent = ({ data, activeFilter, isExport, height = 300 }) => {
    const { t } = useTranslation('analytics');
    const { formatHour, formatWeekDays } = useFormatTimeIndicator();
    const weekDays = formatWeekDays();
    const weekDaysKey = weekDays.join('-');

    const dataConfig = useMemo(() => {
        if (!data) {
            console.log('HeatMapChart: data is null/undefined');
            return null;
        }
        if (!data.graphData) {
            console.warn('HeatMapChart: data.graphData is missing', data);
            return null;
        }
        // Only recalculate if the data really changes
        return data;
    }, [data?.graphData, data?.min, data?.max, data?.id]);

    const chartData = useMemo(() => {
        // Protection: check that the data exists
        if (!dataConfig?.graphData?.length) {
            console.warn('HeatMapChart: no graph data available');
            return [];
        }

        if (!dataConfig.breakpoints || dataConfig.breakpoints.length === 0) {
            console.warn('HeatMapChart: no breakpoints defined', dataConfig);
            return [];
        }

        const average = dataConfig.max - dataConfig.min;

        const finalData = dataConfig.graphData.map((item) => ({
            ...item,
            date: formatHour(item.date)
        }));

        const groupedByDay = weekDays.reduce((acc, day) => {
            acc[day] = [];
            return acc;
        }, {});

        finalData.forEach((item) => {
            if (groupedByDay[item.day]) {
                groupedByDay[item.day].push(item);
            } else {
                console.warn('HeatMapChart: unexpected day in data:', item.day);
            }
        });

        const orderedData = weekDays.flatMap((day) => groupedByDay[day]);

        let firstBreakpoint = undefined;
        // Create a deep copy of breakpoints to avoid modifying read-only objects
        let breakpoints = dataConfig.breakpoints.map((breakpoint) => ({ ...breakpoint }));

        const minGraphValue = Math.min(...dataConfig.graphData.map((item) => item.value));

        if (minGraphValue === 0) {
            // Extract the first breakpoint
            firstBreakpoint = breakpoints.shift();
            // Create a new copy with the modified value
            firstBreakpoint = { ...firstBreakpoint, value: 0 };
        }

        const finalBreakpoints = breakpoints.map((item, index) => ({
            ...item,
            value: dataConfig.min + (index + 1) * (average / breakpoints.length) // Get breakpoints values depending on account's followers
        }));

        if (firstBreakpoint) {
            finalBreakpoints.unshift(firstBreakpoint);
        }

        // Assign color and radius to each data point based on value
        return orderedData.map((dataPoint) => {
            // Find which breakpoint range this value falls into
            const breakpointIndex = finalBreakpoints.findIndex((breakpoint, idx) => {
                const prevValue = idx > 0 ? finalBreakpoints[idx - 1].value : -Infinity;
                return dataPoint.value > prevValue && dataPoint.value <= breakpoint.value;
            });

            // Use found breakpoint or default to last breakpoint for values above range
            const targetBreakpoint =
                breakpointIndex !== -1
                    ? finalBreakpoints[breakpointIndex]
                    : finalBreakpoints[finalBreakpoints.length - 1];

            return {
                ...dataPoint,
                color: targetBreakpoint.color,
                r: breakpointIndex !== -1 ? targetBreakpoint.r : 12.5
            };
        });
    }, [dataConfig, formatHour, weekDaysKey]);

    if (!dataConfig?.graphData?.length) {
        return null;
    }

    return (
        <div className="relative h-full w-full overflow-x-auto" style={{ minHeight: height }}>
            <div className="absolute left-0 top-0 h-full w-full">
                <ResponsiveContainer
                    height={height}
                    width={isExport ? data.fixedSize : '100%'}
                    minWidth={dataConfig.minWidth}>
                    <ScatterChart margin={dataConfig.margin}>
                        <XAxis
                            allowDuplicatedCategory={false}
                            type="category"
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={10}
                            minTickGap={-10}
                        />
                        <YAxis
                            allowDuplicatedCategory={false}
                            type="category"
                            dataKey="day"
                            domain={weekDays}
                            ticks={weekDays}
                            reversed
                            tickFormatter={(day) => t(`commons:days.${day}`).substring(0, 3)}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip
                            cursor={false}
                            animationDuration={0}
                            isAnimationActive={false}
                            content={
                                <TooltipCustom
                                    valueFormat={data.valueFormat}
                                    tooltipTranslation={data.tooltipTranslation}
                                    data={data}
                                    activeFilter={activeFilter}
                                />
                            }
                        />

                        <Scatter
                            data={chartData}
                            shape={<CustomShape dataConfig={dataConfig} isExport={isExport} />}
                        />
                    </ScatterChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

HeatMapChartComponent.propTypes = {
    data: PropTypes.object,
    activeFilter: PropTypes.string,
    height: PropTypes.number
};

const HeatMapChart = ({
    data,
    onReset = () => {
        /** */
    },
    isLoading = false,
    className = '',
    ...restProps
}) => {
    // Protection against missing data when navigating
    if (!data) {
        console.warn('HeatMapChart: data prop is missing');
        return null;
    }

    return (
        <ChartWrapper
            className={className}
            isLoading={isLoading}
            onReset={onReset}
            data={data}
            legendType={data.legendType ?? 'heatMap'}
            {...restProps}>
            <HeatMapChartComponent />
        </ChartWrapper>
    );
};

export { HeatMapChart, HeatMapChartComponent };
